import { sectionLabels } from "../section-labels";
import { colorFields } from "./_shared";
/**
 * Highlight headline — one large sentence whose words are interrupted by
 * pill-shaped photographs, each of which is a link. "Make you look [photo]
 * and feel glowy [photo] and healthy [photo]".
 *
 * Ported from the EasyOrders `ps-hti` section (`data-ps-highlight-text-inline`)
 * found on the `nature` family (2026-07-20). It is a genuinely NEW type, not a
 * variant: nothing we ship interleaves media INTO a line of type. `about` and
 * `banner` both put an image BESIDE or BEHIND copy, and neither has a content
 * model that can express "text, picture, text, picture, text" as an ordered
 * sequence — which is the whole section.
 *
 * The sequence is modelled as ordered BLOCKS rather than a fixed
 * text/image/text/image shape, because their demo runs 3 text runs and 3
 * pills but the markup imposes no ratio: a seller may want two pills, or a
 * pill first. Each block declares its own kind.
 *
 * Their inline stylesheet is the source for the renderer's values — see the
 * "nature" subsection of
 * docs/plans/2026-07-19-storefront-section-vocabulary.md.
 */
export const highlightHeadlineSchema = {
    type: "highlight-headline",
    name: sectionLabels["highlight-headline"],
    description: {
        ar: "جملة كبيرة متقطّعة بصور بيضاوية، كل صورة لينك.",
        en: "One big sentence interrupted by pill-shaped photos, each a link.",
    },
    maxPerTemplate: 2,
    availableOnTemplates: ["home"],
    // Furniture, not a menu item: 1 preset (`nature`), 2 demo instances. Its
    // content model — an ordered run of text, pill photo, text, pill photo — is
    // the hardest thing in the vocabulary to author from empty, and `about`
    // already covers the goal it sits under ("introduces you").
    offeredInAddMenu: false,
    settings: [
        {
            id: "align",
            type: "select",
            label: { ar: "المحاذاة", en: "Alignment" },
            helpText: {
                ar: "الجملة في النص ولا من البداية.",
                en: "Centre the sentence or set it from the leading edge.",
            },
            options: [
                { value: "center", label: { ar: "في النص", en: "Centered" } },
                { value: "start", label: { ar: "من البداية", en: "Start" } },
            ],
            default: "center",
        },
        {
            id: "headingLevel",
            type: "headingLevel",
            label: { ar: "مستوى العنوان", en: "Heading level" },
            default: 2,
        },
        ...colorFields(),
    ],
    blocks: [
        {
            type: "headline-segment",
            name: { ar: "جزء من الجملة", en: "Sentence piece" },
            description: {
                ar: "كلمة/كلمات أو صورة بيضاوية. الترتيب هو ترتيب الجملة.",
                en: "Either some words or one pill photo. Order = reading order.",
            },
            maxPerSection: 8,
            settings: [
                {
                    id: "kind",
                    type: "select",
                    label: { ar: "النوع", en: "Kind" },
                    options: [
                        { value: "text", label: { ar: "كلمات", en: "Words" } },
                        { value: "pill", label: { ar: "صورة", en: "Photo" } },
                    ],
                    default: "text",
                },
                {
                    id: "text",
                    type: "text",
                    i18n: true,
                    label: { ar: "الكلمات", en: "Words" },
                    validate: { max: 60 },
                    showIf: { kind: ["text"] },
                },
                {
                    id: "image",
                    type: "image",
                    label: { ar: "الصورة", en: "Photo" },
                    helpText: {
                        ar: "صورة عرضية؛ بتتقص في شكل بيضاوي.",
                        en: "A landscape photo; it is cropped to a pill.",
                    },
                    focalPoint: true,
                    showIf: { kind: ["pill"] },
                },
                {
                    id: "href",
                    type: "url",
                    label: { ar: "لينك الصورة", en: "Photo link" },
                    helpText: {
                        ar: "الصفحة اللي الصورة بتوديها. سيبها فاضية لو مش عايز لينك.",
                        en: "Where the photo links. Leave empty for no link.",
                    },
                    showIf: { kind: ["pill"] },
                },
                {
                    id: "alt",
                    type: "text",
                    i18n: true,
                    label: { ar: "وصف الصورة", en: "Photo description" },
                    helpText: {
                        ar: "وصف قصير لقارئ الشاشة.",
                        en: "A short description for screen readers.",
                    },
                    validate: { max: 80 },
                    showIf: { kind: ["pill"] },
                },
            ],
        },
    ],
};
//# sourceMappingURL=highlight-headline.js.map